import {Box, Divider, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import * as React from "react";
import {Cell, Pie, PieChart, ResponsiveContainer} from "recharts";
import {directionColor, signed} from "../../../insights/kit/insightsFormat";
import {InsightsTooltip, TooltipDelta, TooltipRow, TooltipTitle} from "../../../insights/kit/InsightsTooltip";
import {BAR_DRAW_DURATION} from "../constants";

export type SentimentKey = "positive" | "neutral" | "negative";

export interface SentimentSlice {
    key: SentimentKey;
    label: string;
    color: string;
    count: number;
    share: number;
    compareCount: number | null;
    compareShare: number | null;
    delta: number | null;
}

export interface SentimentLabels {
    positive: string;
    neutral: string;
    negative: string;
    replies: string;
    count: string;
    compare: string;
    share: string;
    delta: string;
}

/*
 * Sentiment names a category, not a standing: a red slice says the replies read negatively, not that the
 * card missed anything. It reads as the verdict palette on purpose, but it is a palette of its own - moving
 * the verdict colours must not restyle a chart that never judged anything.
 */
export const SENTIMENT_COLORS: Record<SentimentKey, string> = {
    positive: "#0D8157",
    neutral: "#AEB6C4",
    negative: "#B62028"
};

export const lowerIsBetterFor = (slice: SentimentSlice) => slice.key === "negative";

export interface SentimentSpec {
    slices: SentimentSlice[];
    labels: SentimentLabels;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

const SLICE_RADIUS = 4;
const RING_OUTER = "84%";
const RING_INNER = "62%";
const COMPARE_RING_OUTER = "54%";
const COMPARE_RING_INNER = "40%";
const RING_GAP = 4;

const compareFill = (color: string) => alpha(color, 0.35);

interface ActiveSlice {
    slice: SentimentSlice;
    isCompare: boolean;
}

interface SliceTooltipProps extends SentimentSpec {
    active: ActiveSlice;
}

function SliceTooltip({active, labels, formatCount, formatPercent, formatPoints}: SliceTooltipProps) {
    const {slice, isCompare} = active;

    const count = isCompare ? slice.compareCount : slice.count;
    const share = isCompare ? slice.compareShare : slice.share;

    return (
        <React.Fragment>
            <TooltipTitle title={slice.label} subtitle={isCompare ? labels.compare : undefined}/>

            <TooltipRow
                swatch={isCompare ? compareFill(slice.color) : slice.color}
                label={labels.count}
                value={count == null ? "—" : formatCount(count)}
            />

            <TooltipRow label={labels.share} value={share == null ? "—" : formatPercent(share)}/>

            {!isCompare && slice.compareCount != null && (
                <TooltipRow
                    swatch={compareFill(slice.color)}
                    label={labels.compare}
                    value={`${formatCount(slice.compareCount)} · ${formatPercent(slice.compareShare ?? 0)}`}
                />
            )}

            {!isCompare && slice.delta != null && slice.key !== "neutral" && (
                <TooltipDelta
                    label={labels.delta}
                    value={slice.delta}
                    format={formatPoints}
                    lowerIsBetter={lowerIsBetterFor(slice)}
                />
            )}

            {!isCompare && slice.delta != null && slice.key === "neutral" && (
                <TooltipRow label={labels.delta} value={signed(slice.delta, formatPoints).text} color={"text.secondary"}/>
            )}
        </React.Fragment>
    );
}

interface SliceRowProps {
    slice: SentimentSlice;
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

function SliceRow({slice, hasCompare, formatCount, formatPercent, formatPoints}: SliceRowProps) {
    const change = slice.delta == null ? null : signed(slice.delta, formatPoints);

    return (
        <Stack direction={"row"} alignItems={"center"} spacing={1.5} sx={{minWidth: 0}}>
            <Box sx={{width: 12, height: 12, borderRadius: "3px", bgcolor: slice.color, flexShrink: 0}}/>

            <Typography sx={{flex: 1, minWidth: 0, color: "text.primary", fontSize: 14, fontWeight: 600}} noWrap>
                {slice.label}
            </Typography>

            <Typography sx={{color: "text.primary", fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums"}}>
                {formatCount(slice.count)}
            </Typography>

            <Typography
                sx={{
                    minWidth: 52,
                    textAlign: "right",
                    color: "text.secondary",
                    fontSize: 15,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums"
                }}
            >
                {formatPercent(slice.share)}
            </Typography>

            {hasCompare && (
                <Typography
                    component={"span"}
                    sx={{
                        minWidth: 52,
                        textAlign: "right",
                        fontSize: 14,
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color:
                            change == null
                                ? "text.disabled"
                                : slice.key === "neutral"
                                    ? "text.secondary"
                                    : directionColor(change.direction, lowerIsBetterFor(slice))
                    }}
                >
                    {change == null ? "—" : change.text}
                </Typography>
            )}
        </Stack>
    );
}

export function SentimentChart({slices, labels, formatCount, formatPercent, formatPoints}: SentimentSpec) {
    const hasCompare = slices.some(slice => slice.compareCount != null);
    const total = slices.reduce((sum, slice) => sum + slice.count, 0);

    const [active, setActive] = React.useState<ActiveSlice | null>(null);
    const [animate, setAnimate] = React.useState(true);

    React.useEffect(() => {
        setAnimate(true);

        const timer = window.setTimeout(() => setAnimate(false), BAR_DRAW_DURATION + 400);

        return () => window.clearTimeout(timer);
    }, [hasCompare]);

    const enter = (isCompare: boolean) => (_entry: unknown, index: number) => {
        const slice = slices[index];
        setActive(slice == null ? null : {slice, isCompare});
    };

    return (
        <Stack direction={{xs: "column", sm: "row"}} alignItems={"center"} gap={3} sx={{flex: 1, minWidth: 0}}>
            <InsightsTooltip
                followCursor
                placement={"top"}
                title={
                    active == null ? "" : (
                        <SliceTooltip
                            active={active}
                            slices={slices}
                            labels={labels}
                            formatCount={formatCount}
                            formatPercent={formatPercent}
                            formatPoints={formatPoints}
                        />
                    )
                }
            >
                <Box sx={{position: "relative", width: {xs: "100%", sm: 240}, aspectRatio: "1", flexShrink: 0}}>
                    <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                        <PieChart>
                            {hasCompare && (
                                <Pie
                                    data={slices}
                                    dataKey={"compareCount"}
                                    nameKey={"label"}
                                    innerRadius={COMPARE_RING_INNER}
                                    outerRadius={COMPARE_RING_OUTER}
                                    cornerRadius={SLICE_RADIUS}
                                    paddingAngle={RING_GAP}
                                    stroke={"none"}
                                    isAnimationActive={animate}
                                    animationDuration={BAR_DRAW_DURATION}
                                    animationEasing={"ease-out"}
                                    onMouseEnter={enter(true)}
                                    onMouseLeave={() => setActive(null)}
                                >
                                    {slices.map(slice => (
                                        <Cell key={slice.key} fill={compareFill(slice.color)}/>
                                    ))}
                                </Pie>
                            )}

                            <Pie
                                data={slices}
                                dataKey={"count"}
                                nameKey={"label"}
                                innerRadius={RING_INNER}
                                outerRadius={RING_OUTER}
                                cornerRadius={SLICE_RADIUS}
                                paddingAngle={RING_GAP}
                                stroke={"none"}
                                isAnimationActive={animate}
                                animationDuration={BAR_DRAW_DURATION}
                                animationEasing={"ease-out"}
                                onMouseEnter={enter(false)}
                                onMouseLeave={() => setActive(null)}
                            >
                                {slices.map(slice => (
                                    <Cell key={slice.key} fill={slice.color}/>
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>

                    <Stack
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{position: "absolute", inset: 0, pointerEvents: "none"}}
                    >
                        <Typography sx={{color: "text.primary", fontSize: 22, fontWeight: 750, fontVariantNumeric: "tabular-nums"}}>
                            {formatCount(total)}
                        </Typography>

                        <Typography sx={{color: "text.secondary", fontSize: 13}}>{labels.replies}</Typography>
                    </Stack>
                </Box>
            </InsightsTooltip>

            <Stack gap={1.5} sx={{flex: 1, minWidth: 0, width: "100%"}}>
                {slices.map((slice, index) => (
                    <React.Fragment key={slice.key}>
                        <SliceRow
                            slice={slice}
                            hasCompare={hasCompare}
                            formatCount={formatCount}
                            formatPercent={formatPercent}
                            formatPoints={formatPoints}
                        />

                        {index < slices.length - 1 && <Divider/>}
                    </React.Fragment>
                ))}
            </Stack>
        </Stack>
    );
}
